// src/components/resources/tools/generators/AdMockup/FacebookAd.tsx
'use client'

import {
  HandThumbUpIcon,
  ChatBubbleOvalLeftIcon,
  ArrowUturnRightIcon,
  GlobeAmericasIcon,
  EllipsisHorizontalIcon,
} from '@heroicons/react/24/outline'
import {
  HandThumbUpIcon as HandThumbUpSolidIcon,
  HeartIcon as HeartSolidIcon,
  CheckBadgeIcon as CheckBadgeSolidIcon,
} from '@heroicons/react/24/solid'
import { clsx } from 'clsx'
import { Avatar } from './Avatar'
import { CreativeSlot } from './CreativeSlot'
import { formatCount } from './ad-format'
import type { AdState } from './types'

export function FacebookAd({ state, creativeImage, avatarImage }: { state: AdState; creativeImage: string | null; avatarImage: string | null }): JSX.Element {
  const dark = state.theme === 'dark'
  return (
    <div className={clsx('overflow-hidden rounded-xl border font-sans shadow-md', dark ? 'border-slate-700 bg-slate-900 text-slate-100' : 'border-slate-200 bg-white text-slate-900')}>
      {/* header */}
      <div className="flex items-center gap-3 px-4 pt-3">
        <Avatar name={state.pageName} image={avatarImage} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1">
            <span className="truncate text-[15px] font-semibold">{state.pageName}</span>
            {state.verified && <CheckBadgeSolidIcon className="h-4 w-4 flex-shrink-0 text-[#1877F2]" aria-label="Verified" />}
          </div>
          <div className={clsx('flex items-center gap-1 text-xs', dark ? 'text-slate-400' : 'text-slate-500')}>
            <span>Sponsored</span>
            <span aria-hidden="true">·</span>
            <GlobeAmericasIcon className="h-3.5 w-3.5" />
          </div>
        </div>
        <EllipsisHorizontalIcon className={clsx('h-5 w-5 flex-shrink-0', dark ? 'text-slate-400' : 'text-slate-500')} />
      </div>

      {/* primary text */}
      <p className="whitespace-pre-wrap px-4 py-3 text-sm leading-snug">{state.primaryText}</p>

      {/* creative (1.91:1 link-ad ratio) */}
      <CreativeSlot image={creativeImage} alt={`${state.pageName} ad creative`} ratio={1 / 1.91} />

      {/* link card */}
      <div className={clsx('flex items-center justify-between gap-3 px-4 py-3', dark ? 'bg-slate-800' : 'bg-slate-100')}>
        <div className="min-w-0">
          <div className={clsx('truncate text-[11px] uppercase tracking-wide', dark ? 'text-slate-400' : 'text-slate-500')}>{state.displayUrl}</div>
          <div className="truncate text-[15px] font-semibold leading-tight">{state.headline}</div>
          <div className={clsx('truncate text-xs', dark ? 'text-slate-400' : 'text-slate-500')}>{state.description}</div>
        </div>
        <span className={clsx('flex-shrink-0 rounded-md px-3 py-1.5 text-xs font-semibold', dark ? 'bg-slate-700 text-slate-100' : 'bg-slate-200 text-slate-800')}>
          {state.cta}
        </span>
      </div>

      {/* engagement summary */}
      <div className={clsx('flex items-center justify-between px-4 py-2 text-xs', dark ? 'text-slate-400' : 'text-slate-500')}>
        <span className="flex items-center gap-1.5">
          <span className="flex">
            <span className={clsx('z-10 flex h-4 w-4 items-center justify-center rounded-full bg-[#1877F2] ring-1', dark ? 'ring-slate-900' : 'ring-white')}>
              <HandThumbUpSolidIcon className="h-2.5 w-2.5 text-white" />
            </span>
            <span className={clsx('-ml-1 flex h-4 w-4 items-center justify-center rounded-full bg-[#F3425F] ring-1', dark ? 'ring-slate-900' : 'ring-white')}>
              <HeartSolidIcon className="h-2.5 w-2.5 text-white" />
            </span>
          </span>
          {formatCount(state.likes)}
        </span>
        <span>{formatCount(state.comments)} comments · {formatCount(state.shares)} shares</span>
      </div>

      {/* action bar */}
      <div className={clsx('mx-3 flex items-center justify-around border-t py-1.5 text-sm font-medium', dark ? 'border-slate-700 text-slate-300' : 'border-slate-200 text-slate-600')}>
        <span className="flex items-center gap-1.5"><HandThumbUpIcon className="h-[18px] w-[18px]" /> Like</span>
        <span className="flex items-center gap-1.5"><ChatBubbleOvalLeftIcon className="h-[18px] w-[18px]" /> Comment</span>
        <span className="flex items-center gap-1.5"><ArrowUturnRightIcon className="h-[18px] w-[18px]" /> Share</span>
      </div>
    </div>
  )
}
